import AwsS3 from "@uppy/aws-s3";
import Uppy from "@uppy/core";
import { isEqual, merge } from "lodash";
import {
  createContext,
  PropsWithChildren,
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { BehaviorSubject, distinctUntilChanged, map } from "rxjs";
import { Store, UploadStore } from "../store/UploadStore";
import { UploaderEventMap } from "../types/UploaderEventMap";
import {
  StorageApiUploader,
  UploaderFileBody,
  type UploaderFile,
  type UploaderFileMeta,
  type UploaderState,
} from "../types/UploaderFile";
import { ThumbnailGenerator } from "../uppy-plugins/ThumbnailGenerator";
import { useStorageApiUploadOptions } from "./StorageApiOptionsContext";

type UppyListeners = {
  onFileAdded?: UploaderEventMap["file-added"];
  onThumbnailGenerated?: UploaderEventMap["thumbnail:generated"];
  onFileUploaded?: UploaderEventMap["upload-success"];
  onUploadError?: UploaderEventMap["upload-error"];
};

type InstanceOptionsBase = {
  onBeforeFileAdded?: (file: UploaderFile) => UploaderFile | boolean | undefined;
  onFileAdded?: UploaderEventMap["file-added"];
  onThumbnailGenerated?: UploaderEventMap["thumbnail:generated"];
  /**
   * A list of file extensions to allow, lowercase and prefixed with a dot (e.g. `.png`).
   * A call that passes a list applies it to the scope's instance, new or existing; a call that
   * omits it keeps the current list, so an instance created without one allows all files.
   */
  allowedExtensions?: string[];
  instanceKeyModifier?: string;
};

export type StorageApiInstanceOptions = InstanceOptionsBase & {
  onFileUploaded?: UploaderEventMap["upload-success"];
  onUploadError?: UploaderEventMap["upload-error"];
};

export type InstanceOptions = StorageApiInstanceOptions;

type PartialInstanceOptions = Partial<StorageApiInstanceOptions>;

type InstanceGetter = (scope: string, options?: PartialInstanceOptions) => StorageApiUploader;

interface UploadContext {
  store: Store;
  instances: Map<string, StorageApiUploader>;
  getInstance: InstanceGetter;
  filesSaving: number;
  updateFilesSaving: (delta: number) => void;
}

const Context = createContext<UploadContext | undefined>(undefined);

const useUploadContext = (): UploadContext | undefined => useContext(Context);

export function UploadContextProvider({ children }: PropsWithChildren): ReactElement {
  // Uppy uses `Object.hasOwn` internally which is not supported in some older browsers.
  // In these cases we fall back to `Object.hasOwnProperty` which has been well-supported since the Mesozoic Era.
  if (!Object.hasOwn) {
    // FIXME

    Object.hasOwn = (o: object, v: PropertyKey) => Object.prototype.hasOwnProperty.call(o, v);
  }

  const store = useRef<Store>(new BehaviorSubject({}));
  const instances = useRef<Map<string, StorageApiUploader>>(new Map([]));
  const eventListeners = useRef<Map<string, UppyListeners>>(new Map([]));
  const beforeFileAddedOptions = useRef<Map<string, InstanceOptionsBase["onBeforeFileAdded"]>>(
    new Map([]),
  );
  const storageApiOptions = useStorageApiUploadOptions();

  const [filesSaving, setFilesSaving] = useState(0);

  if (!storageApiOptions) {
    throw new Error(
      "UploadContextProvider: storage upload options are required via StorageApiUploadOptionsProvider!",
    );
  }

  const registerListeners = useCallback(
    (
      uploader: StorageApiUploader,
      { onFileAdded, onThumbnailGenerated, onFileUploaded, onUploadError }: UppyListeners,
    ) => {
      if (onFileAdded) {
        uploader.on("file-added", onFileAdded);
      }

      if (onThumbnailGenerated) {
        uploader.on("thumbnail:generated", onThumbnailGenerated);
      }

      if (onFileUploaded) {
        uploader.on("upload-success", onFileUploaded);
      }

      if (onUploadError) {
        uploader.on("upload-error", onUploadError);
      }
    },
    [],
  );

  const getInstance: InstanceGetter = useCallback(
    (
      scope,
      {
        allowedExtensions,
        onBeforeFileAdded,
        onFileAdded,
        onThumbnailGenerated,
        onFileUploaded: onFileUploadedParam,
        onUploadError,
        instanceKeyModifier,
      }: PartialInstanceOptions = {},
    ) => {
      const instanceKey = instanceKeyModifier ? `${scope}.${instanceKeyModifier}` : scope;

      let instance = instances.current.get(instanceKey);
      const onFileUploaded: UploaderEventMap["upload-success"] | undefined = onFileUploadedParam
        ? async (file, body) => {
            setFilesSaving((files) => files + 1);

            try {
              await onFileUploadedParam(file, body);
            } catch (err) {
              const uploadError = err instanceof Error ? err : new Error(String(err));
              instance?.emit("upload-error", file, uploadError);
              if (!onUploadError) {
                console.error(uploadError);
              }
            } finally {
              setFilesSaving((files) => files - 1);
            }
          }
        : undefined;

      beforeFileAddedOptions.current.set(instanceKey, onBeforeFileAdded);
      if (instance) {
        // A list can change after the instance exists (one gated on a feature flag that resolves
        // late). setOptions notifies the store, so it runs only when the list actually changes.
        if (
          allowedExtensions &&
          !isEqual(allowedExtensions, instance.opts.restrictions.allowedFileTypes)
        ) {
          instance.setOptions({ restrictions: { allowedFileTypes: allowedExtensions } });
        }

        // We need to update the event listeners
        // i.e. unregister the old listeners and register new ones
        const listeners = eventListeners.current.get(instanceKey);
        if (listeners && listeners.onFileAdded) {
          instance.off("file-added", listeners.onFileAdded);
        }
        if (listeners && listeners.onThumbnailGenerated) {
          instance.off("thumbnail:generated", listeners.onThumbnailGenerated);
        }
        if (listeners && listeners.onFileUploaded) {
          instance.off("upload-success", listeners.onFileUploaded);
        }
        if (listeners && listeners.onUploadError) {
          instance.off("upload-error", listeners.onUploadError);
        }
        registerListeners(instance, {
          onFileAdded,
          onThumbnailGenerated,
          onFileUploaded,
          onUploadError,
        });

        instances.current.set(instanceKey, instance);
        eventListeners.current.set(instanceKey, {
          onFileAdded,
          onThumbnailGenerated,
          onFileUploaded,
          onUploadError,
        });
        return instance;
      }

      instance = new Uppy<UploaderFileMeta, UploaderFileBody>({
        id: scope,
        autoProceed: true,
        store: new UploadStore({ id: scope, store: store.current }),
        onBeforeFileAdded(file) {
          const beforeAdd = beforeFileAddedOptions.current.get(instanceKey);

          return beforeAdd ? beforeAdd(file) : file;
        },
        restrictions: {
          // S3 has a 5GB limit (https://aws.amazon.com/s3/faqs/#:~:text=the%20largest%20object%20that%20can,single%20PUT%20is%205%20GB.)
          maxFileSize: 5e9,
          allowedFileTypes: allowedExtensions,
        },
      })
        .use(ThumbnailGenerator)
        .use(AwsS3<UploaderFileMeta, UploaderFileBody>, storageApiOptions);

      registerListeners(instance, {
        onFileAdded,
        onThumbnailGenerated,
        onFileUploaded,
        onUploadError,
      });

      instances.current.set(instanceKey, instance);
      eventListeners.current.set(instanceKey, {
        onFileAdded,
        onThumbnailGenerated,
        onFileUploaded,
        onUploadError,
      });

      return instance;
    },
    [storageApiOptions, registerListeners],
  );

  const value = useMemo(
    () => ({
      store: store.current,
      instances: instances.current,
      getInstance,
      filesSaving,
      updateFilesSaving: (delta: number) => setFilesSaving((files) => files + delta),
    }),
    [filesSaving, getInstance],
  );
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

const defaultOptions: Partial<StorageApiInstanceOptions> = {
  onBeforeFileAdded: (x) => x,
};

export function useUploader(
  scope: string,
  options?: Partial<StorageApiInstanceOptions>,
): StorageApiUploader;
export function useUploader(
  scope: string,
  options: PartialInstanceOptions = {},
): StorageApiUploader {
  const context = useUploadContext();

  if (!context) {
    throw new Error("useUploader must be used within an UploadContext!");
  }

  const { getInstance } = context;
  return useMemo(
    () => getInstance(scope, merge({}, defaultOptions, options)),
    [scope, options, getInstance],
  );
}

export const useUploaderInstance = (scope: string): StorageApiUploader | undefined => {
  const context = useUploadContext();

  if (!context) {
    throw new Error("useUploaderInstance must be used within an UploadContext!");
  }

  const { instances } = context;
  return instances.get(scope);
};

export function useUploaderEvent<K extends keyof UploaderEventMap>(
  instance: StorageApiUploader | undefined,
  event: K,
  callback?: UploaderEventMap[K],
): void {
  useEffect(() => {
    if (callback) {
      instance?.on(event as keyof UploaderEventMap, callback as never);
    }

    return () => {
      if (callback) {
        instance?.off(event as keyof UploaderEventMap, callback as never);
      }
    };
  }, [instance, event, callback]);
}

type UseUploadStateOptions<T> = {
  selector?: (x: Record<string, UploaderState>) => T;
};

const defaultSelector = (x: Record<string, UploaderState>) => x;

export function useUploadState(): Record<string, UploaderState>;
export function useUploadState<T>({ selector }: UseUploadStateOptions<T>): T;
export function useUploadState<T>({ selector }: UseUploadStateOptions<T> = {}):
  | Record<string, UploaderState>
  | T {
  const uploadContext = useUploadContext();

  if (!uploadContext) {
    throw new Error("useUploadState must be used within an UploadContext!");
  }

  const { store } = uploadContext;

  const selectorRef = useRef(selector ?? defaultSelector);

  const [uploadState, setUploadState] = useState(selectorRef.current(store.getValue()));

  useEffect(() => {
    const subscription = store
      .pipe(
        map((x) => selectorRef.current(x)),
        distinctUntilChanged(isEqual),
      )
      .subscribe(setUploadState);

    return () => subscription.unsubscribe();
  }, [store]);

  return uploadState;
}

export const useSavingInProgress = (): boolean => {
  const uploadContext = useUploadContext();

  if (!uploadContext) {
    throw new Error("useSavingInProgress must be used within an UploadContext!");
  }

  return uploadContext.filesSaving > 0;
};
